import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const GrandTotalCard = () => {
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fetchingForDate, setFetchingForDate] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [apiBreakdown, setApiBreakdown] = useState({});
  const [googleAdsData, setGoogleAdsData] = useState({
    totalCost: 0,
    totalClicks: 0,
    averageCpc: 0,
    success: false,
    loading: false
  });
  const [selectedDate, setSelectedDate] = useState('');
  const [isDatePickerMode, setIsDatePickerMode] = useState(false);
  const [datePickerData, setDatePickerData] = useState(null);

  // Helper function to get yesterday's date in Pacific timezone
  const getYesterdayInPacific = () => {
    const now = new Date();
    const pacificTime = new Date(now.toLocaleString("en-US", {timeZone: "America/Los_Angeles"}));
    pacificTime.setDate(pacificTime.getDate() - 1);
    const year = pacificTime.getFullYear();
    const month = String(pacificTime.getMonth() + 1).padStart(2, '0');
    const day = String(pacificTime.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to format date for display
  const formatDateForDisplay = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get data for specific date from MongoDB
  const getDataForDate = useCallback(async (targetDate) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`/api/all-apis-data/${targetDate}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Date-specific data from database:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('Error fetching data for date:', error);
      throw error;
    }
  }, []);

  // Fetch fresh data for specific date (store in MongoDB first)
  const fetchDataForDate = useCallback(async (targetDate) => {
    setFetchingForDate(true);
    
    try {
      const token = localStorage.getItem('token');
      
      console.log(`Fetching fresh data for date: ${targetDate}`);
      
      const response = await axios.post('/api/fetch-all-apis-for-date', {
        targetDate: targetDate
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Fresh data fetch response:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('Error fetching fresh data for date:', error);
      throw error;
    } finally {
      setFetchingForDate(false);
    }
  }, []);

  // Handle date picker change
  const handleDateChange = async (event) => {
    const newDate = event.target.value;
    setSelectedDate(newDate);
    
    if (newDate) {
      setLoading(true);
      setError('');
      
      try {
        // First try to get existing data from MongoDB
        const existingData = await getDataForDate(newDate);
        
        // Process the data for display
        const breakdown = {};
        const apiMapping = {
          'madrivo': 'Madrivo',
          'diablo': 'Diablo', 
          'brightreach': 'BrightReach',
          'banneredge': 'BannerEdge',
          'shift44': 'Shift44',
          'leadgroup': 'LeadGroup'
        };
        
        for (const [key, displayName] of Object.entries(apiMapping)) {
          const apiData = existingData.revenueApis[key];
          if (apiData) {
            breakdown[displayName] = {
              revenue: apiData.revenue || 0,
              conversions: apiData.conversions || 0,
              error: !apiData.success
            };
          } else {
            breakdown[displayName] = {
              revenue: 0,
              conversions: 0,
              error: true
            };
          }
        }
        
        setDatePickerData({
          totalRevenue: existingData.totalRevenue || 0,
          apiBreakdown: breakdown,
          googleAds: existingData.googleAds,
          targetDate: newDate,
          fromDatabase: existingData.fromDatabase,
          fetchedAt: new Date()
        });
        
        setIsDatePickerMode(true);
        
      } catch (error) {
        setError(`Error loading data for ${newDate}: ${error.message}`);
        setDatePickerData(null);
      } finally {
        setLoading(false);
      }
    } else {
      // Reset to yesterday mode
      setIsDatePickerMode(false);
      setDatePickerData(null);
      await fetchGrandTotal();
    }
  };

  // Handle "Fetch Fresh Data" for selected date
  const handleFetchForDate = async () => {
    if (!selectedDate) return;
    
    setLoading(true);
    setError('');
    
    try {
      // Fetch fresh data (this will store in MongoDB first)
      const freshData = await fetchDataForDate(selectedDate);
      
      // Process the response for display
      const breakdown = {};
      const apiMapping = {
        'madrivo': 'Madrivo',
        'diablo': 'Diablo', 
        'brightreach': 'BrightReach',
        'banneredge': 'BannerEdge',
        'shift44': 'Shift44',
        'leadgroup': 'LeadGroup'
      };
      
      for (const [key, displayName] of Object.entries(apiMapping)) {
        const apiData = freshData.revenueApis[key];
        if (apiData) {
          breakdown[displayName] = {
            revenue: apiData.revenue || 0,
            conversions: apiData.conversions || 0,
            error: !apiData.success
          };
        } else {
          breakdown[displayName] = {
            revenue: 0,
            conversions: 0,
            error: true
          };
        }
      }
      
      setDatePickerData({
        totalRevenue: freshData.totalRevenue || 0,
        apiBreakdown: breakdown,
        googleAds: freshData.googleAds,
        targetDate: selectedDate,
        fromDatabase: false,
        fetchedAt: freshData.fetchedAt,
        processingTime: freshData.processingTime,
        errors: freshData.errors
      });
      
    } catch (error) {
      setError(`Error fetching fresh data for ${selectedDate}: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchGoogleAdsData = useCallback(async () => {
    setGoogleAdsData(prev => ({ ...prev, loading: true }));
    
    try {
      const token = localStorage.getItem('token');
      const yesterday = getYesterdayInPacific();
      
      const response = await axios.get('/api/google-ads-data', {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        params: {
          targetDate: yesterday
        }
      });
      
      console.log('Google Ads API response:', response.data);
      
      setGoogleAdsData({
        totalCost: response.data.totalCost || 0,
        totalClicks: response.data.totalClicks || 0,
        averageCpc: response.data.averageCpc || 0,
        campaignCount: response.data.campaignCount || 0,
        success: response.data.success || false,
        loading: false,
        error: response.data.success ? null : response.data.error,
        fromDatabase: response.data.fromDatabase || false,
        fetchedAt: response.data.fetchedAt || null,
        processingTime: response.data.processingTime || 0
      });
      
    } catch (error) {
      console.error('Error fetching Google Ads data:', error);
      setGoogleAdsData({
        totalCost: 0,
        totalClicks: 0,
        averageCpc: 0,
        campaignCount: 0,
        success: false,
        loading: false,
        error: error.response?.data?.message || 'Failed to fetch Google Ads data',
        fromDatabase: false
      });
    }
  }, []);

  const fetchFreshGoogleAdsData = useCallback(async () => {
    setGoogleAdsData(prev => ({ ...prev, loading: true }));
    
    try {
      const token = localStorage.getItem('token');
      const yesterday = getYesterdayInPacific();
      
      console.log('Fetching fresh Google Ads data from API...');
      
      const response = await axios.post('/api/fetch-google-ads-data', {
        targetDate: yesterday
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Fresh Google Ads API response:', response.data);
      
      setGoogleAdsData({
        totalCost: response.data.totalCost || 0,
        totalClicks: response.data.totalClicks || 0,
        averageCpc: response.data.averageCpc || 0,
        campaignCount: response.data.campaignCount || 0,
        success: response.data.success || false,
        loading: false,
        error: response.data.success ? null : response.data.error,
        fromDatabase: false,
        fetchedAt: new Date(),
        processingTime: response.data.processingTime || 0
      });
      
    } catch (error) {
      console.error('Error fetching fresh Google Ads data:', error);
      setGoogleAdsData({
        totalCost: 0,
        totalClicks: 0,
        averageCpc: 0,
        campaignCount: 0,
        success: false,
        loading: false,
        error: error.response?.data?.message || 'Failed to fetch fresh Google Ads data',
        fromDatabase: false
      });
    }
  }, []);

  const fetchGrandTotal = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const yesterday = getYesterdayInPacific();
      
      // List of all APIs
      const apiSources = ['Madrivo', 'Diablo', 'BrightReach', 'BannerEdge', 'Shift44', 'LeadGroup'];
      
      let grandTotal = 0;
      const breakdown = {};
      
      // Fetch data for each API source
      for (const source of apiSources) {
        try {
          const response = await axios.get('/api/revenue-data', {
            headers: {
              'Authorization': `Bearer ${token}`
            },
            params: {
              source: source
            }
          });
          
          // Filter data to only yesterday's records
          const yesterdayData = response.data.filter(item => {
            const itemDateString = item.date.split('T')[0];
            return itemDateString === yesterday;
          });
          
          // Calculate total revenue for this API
          const apiTotal = yesterdayData.reduce((total, item) => total + item.revenue, 0);
          
          breakdown[source] = {
            revenue: apiTotal,
            conversions: yesterdayData.length
          };
          
          grandTotal += apiTotal;
          
        } catch (apiError) {
          console.error(`Error fetching data for ${source}:`, apiError);
          breakdown[source] = {
            revenue: 0,
            conversions: 0,
            error: true
          };
        }
      }
      
      setTotalRevenue(grandTotal);
      setApiBreakdown(breakdown);
      setLastUpdate(new Date());
      
      // Also fetch Google Ads data
      await fetchGoogleAdsData();
      
    } catch (error) {
      setError('Failed to fetch grand total');
      console.error('Error fetching grand total:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchGoogleAdsData]);

  const triggerRefresh = async () => {
    if (isDatePickerMode && selectedDate) {
      await handleDateChange({ target: { value: selectedDate } });
    } else {
      await fetchGrandTotal();
    }
  };

  useEffect(() => {
    if (!isDatePickerMode) {
      fetchGrandTotal();
    }
  }, [fetchGrandTotal, isDatePickerMode]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Get current data based on mode
  const getCurrentData = () => {
    if (isDatePickerMode && datePickerData) {
      return {
        totalRevenue: datePickerData.totalRevenue,
        apiBreakdown: datePickerData.apiBreakdown,
        googleAds: datePickerData.googleAds,
        targetDate: datePickerData.targetDate,
        fromDatabase: datePickerData.fromDatabase,
        fetchedAt: datePickerData.fetchedAt,
        processingTime: datePickerData.processingTime
      };
    } else {
      return {
        totalRevenue: totalRevenue,
        apiBreakdown: apiBreakdown,
        googleAds: googleAdsData,
        targetDate: getYesterdayInPacific(),
        fromDatabase: googleAdsData.fromDatabase,
        fetchedAt: lastUpdate
      };
    }
  };

  const currentData = getCurrentData();

  // Calculate profit (Revenue - Google Ads Cost)
  const calculateProfit = () => {
    return currentData.totalRevenue - (currentData.googleAds?.totalCost || 0);
  };

  // Calculate ROI (Return on Investment)
  const calculateROI = () => {
    const adCost = currentData.googleAds?.totalCost || 0;
    if (adCost === 0) return 0;
    return ((currentData.totalRevenue - adCost) / adCost) * 100;
  };

  return (
    <div className="dashboard-card grand-total-card mb-4" style={{ background: 'linear-gradient(135deg,rgb(0, 162, 255) 0%,rgb(12, 72, 122) 100%)', color: 'white', position: 'relative' }}>
      <div className="card-header" style={{ borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
        <div className="d-flex align-items-center justify-content-center justify-content-md-start">
          <span style={{ fontSize: '24px', marginRight: '12px' }}><i className="fa-solid fa-dollar-sign"></i></span>
          <div className="text-center text-md-start">
            <h4 className="mb-0" style={{ color: 'white' }}>Revenue & Ad Spend</h4>
            <small style={{ color: 'rgba(0, 0, 0, 0.8)', fontWeight: 'bold' }}>
              {isDatePickerMode && selectedDate ? (
                <><i className="fa-solid fa-calendar me-1"></i> {formatDateForDisplay(selectedDate)} - Custom Date</>
              ) : (
                <>Yesterday ({getYesterdayInPacific()}) - All APIs + Google Ads</>
              )}
            </small>
          </div>
        </div>
      </div>
      
      {/* Mobile Date Picker and Buttons - Under header */}
      <div className="d-block d-md-none" style={{ 
        padding: '16px 20px', 
        borderBottom: '1px solid rgba(255,255,255,0.1)' 
      }}>
        <div style={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          flexWrap: 'wrap'
        }}>
          {/* Date Picker */}
          <input
            type="date"
            className="form-control form-control-sm"
            value={selectedDate}
            onChange={handleDateChange}
            style={{
              backgroundColor: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: 'white',
              width: '140px',
              flex: '0 0 auto'
            }}
            max={new Date().toISOString().split('T')[0]}
          />
          
          {/* Fetch Fresh Data for Selected Date */}
          {isDatePickerMode && selectedDate && (
            <button 
              className="btn btn-sm btn-outline-light"
              onClick={handleFetchForDate}
              disabled={loading || fetchingForDate}
              style={{ borderColor: 'rgba(255,255,255,0.5)', fontSize: '12px', flex: '0 0 auto' }}
              title="Fetch fresh data for selected date (stores in MongoDB first)"
            >
              {fetchingForDate ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" style={{ width: '10px', height: '10px' }}></span>
                  Fetching...
                </>
              ) : (
                <><i className="fa-solid fa-rotate me-1"></i>Fetch Fresh</>
              )}
            </button>
          )}
          
          {/* Regular Refresh Button */}
          <button 
            className="btn btn-sm btn-outline-light"
            onClick={triggerRefresh}
            disabled={loading}
            style={{ borderColor: 'rgba(255,255,255,0.5)', flex: '0 0 auto' }}
          >
            {loading ? (
              <span className="spinner-border spinner-border-sm" role="status"></span>
            ) : (
              <i className="fa-solid fa-rotate"></i>
            )}
          </button>
        </div>
      </div>
      
      {/* Date Picker and Buttons - Right Side (Desktop only) */}
      <div className="d-none d-md-flex" style={{ 
        position: 'absolute', 
        top: '30px', 
        right: '20px', 
        zIndex: 10,
        alignItems: 'center',
        gap: '8px'
      }}>
        {/* Date Picker */}
        <input
          type="date"
          className="form-control form-control-sm"
          value={selectedDate}
          onChange={handleDateChange}
          style={{
            backgroundColor: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.3)',
            color: 'white',
            width: '140px'
          }}
          max={new Date().toISOString().split('T')[0]}
        />
        
        {/* Fetch Fresh Data for Selected Date */}
        {isDatePickerMode && selectedDate && (
          <button 
            className="btn btn-sm btn-outline-light"
            onClick={handleFetchForDate}
            disabled={loading || fetchingForDate}
            style={{ borderColor: 'rgba(255,255,255,0.5)', fontSize: '12px' }}
            title="Fetch fresh data for selected date (stores in MongoDB first)"
          >
            {fetchingForDate ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" style={{ width: '10px', height: '10px' }}></span>
                Fetching...
              </>
            ) : (
              <><i className="fa-solid fa-rotate me-1"></i>Fetch Fresh</>
            )}
          </button>
        )}
        
        {/* Regular Refresh Button */}
        <button 
          className="btn btn-sm btn-outline-light"
          onClick={triggerRefresh}
          disabled={loading}
          style={{ borderColor: 'rgba(255,255,255,0.5)' }}
        >
          {loading ? (
            <span className="spinner-border spinner-border-sm" role="status"></span>
          ) : (
            <i className="fa-solid fa-rotate"></i>
          )}
        </button>
      </div>
      
      <div className="card-content">
        {error && (
          <div className="alert alert-warning alert-sm mb-3" style={{ background: 'rgba(255,193,7,0.2)', border: '1px solid rgba(255,193,7,0.5)', color: 'white' }}>
            {error}
          </div>
        )}
        
        {loading && currentData.totalRevenue === 0 ? (
          <div className="text-center py-4">
            <div className="spinner-border text-light" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2" style={{ color: 'rgba(255,255,255,0.8)' }}>
              {fetchingForDate ? 'Fetching fresh data from APIs...' : 'Calculating totals...'}
            </p>
          </div>
        ) : (
          <div>
            {/* Main Metrics Row */}
            <div className="row text-center py-3">
              <div className="col-md-3 mb-3">
                <div className="display-4 fw-bold mb-1" style={{ color: 'white', textShadow: '2px 2px 4px rgba(0,0,0,0.3)', fontSize: '40px' }}>
                  {formatCurrency(currentData.totalRevenue)}
                </div>
                <p className="mb-0 small" style={{ color: 'rgba(255,255,255,0.9)' }}>
                  Total Revenue
                </p>
              </div>
              
              <div className="col-md-3 mb-3">
                <div className="display-4 fw-bold mb-1" style={{ color: '#ffcccb', textShadow: '2px 2px 4px rgba(0,0,0,0.3)', fontSize: '40px' }}>
                  {formatCurrency(currentData.googleAds?.totalCost || 0)}
                </div>
                <p className="mb-0 small" style={{ color: 'rgba(255,255,255,0.9)' }}>
                  Ad Spend
                  {currentData.googleAds?.loading && <span className="spinner-border spinner-border-sm ms-1" style={{ width: '12px', height: '12px' }}></span>}
                </p>
              </div>
              
              <div className="col-md-3 mb-3">
                <div className="display-4 fw-bold mb-1" style={{ 
                  color: calculateProfit() >= 0 ? '#90EE90' : '#ffcccb', 
                  textShadow: '2px 2px 4px rgba(0,0,0,0.3)', fontSize: '40px' 
                }}>
                  {formatCurrency(calculateProfit())}
                </div>
                <p className="mb-0 small" style={{ color: 'rgba(255,255,255,0.9)' }}>
                  Profit
                </p>
              </div>
              
              <div className="col-md-3 mb-3">
                <div className="display-4 fw-bold mb-1" style={{ 
                  color: calculateROI() >= 0 ? '#90EE90' : '#ffcccb', 
                  textShadow: '2px 2px 4px rgba(0,0,0,0.3)' , fontSize: '40px'
                }}>
                  {calculateROI().toFixed(1)}%
                </div>
                <p className="mb-0 small" style={{ color: 'rgba(255,255,255,0.9)' }}>
                  ROI
                </p>
              </div>
            </div>

            {/* Google Ads Details */}
            {currentData.googleAds?.success && (
              <div className="row text-center py-2" style={{ borderTop: '1px solid rgba(255,255,255,0.2)', borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                <div className="col-md-3 mb-2">
                  <div className="h5 mb-0" style={{ color: 'white' }}>
                    {(currentData.googleAds.totalClicks || 0).toLocaleString()}
                  </div>
                  <small style={{ color: 'rgba(255,255,255,0.8)' }}>Total Clicks</small>
                </div>
                <div className="col-md-3 mb-2">
                  <div className="h5 mb-0" style={{ color: 'white' }}>
                    {formatCurrency(currentData.googleAds.averageCpc || 0)}
                  </div>
                  <small style={{ color: 'rgba(255,255,255,0.8)' }}>Avg CPC</small>
                </div>
                <div className="col-md-3 mb-2">
                  <div className="h5 mb-0" style={{ color: 'white' }}>
                    {currentData.googleAds.campaignCount || 0}
                  </div>
                  <small style={{ color: 'rgba(255,255,255,0.8)' }}>Campaigns</small>
                </div>
                <div className="col-md-3 mb-2">
                  {!isDatePickerMode && (
                    <button 
                      className="btn btn-sm btn-outline-light"
                      onClick={fetchFreshGoogleAdsData}
                      disabled={currentData.googleAds?.loading}
                      style={{ borderColor: 'rgba(255,255,255,0.5)', fontSize: '12px' }}
                      title="Fetch fresh Google Ads data from API (takes ~80 seconds)"
                    >
                      {currentData.googleAds?.loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" style={{ width: '10px', height: '10px' }}></span>
                          Fetching...
                        </>
                      ) : (
                        <><i className="fa-solid fa-rotate me-1"></i>Fetch Fresh Ads Data</>
                      )}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Google Ads Status/Info */}
            {currentData.googleAds?.success && (
              <div className="text-center py-1" style={{ borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                <small style={{ color: 'rgba(255,255,255,0.7)' }}>
                  {currentData.fromDatabase ? (
                    <>
                      📊 Data from database • Fetched: {(() => {
                        try {
                          return currentData.googleAds.fetchedAt ? new Date(currentData.googleAds.fetchedAt).toLocaleString() : 'Unknown';
                        } catch (e) {
                          return 'Unknown';
                        }
                      })()}
                      {currentData.googleAds.processingTime > 0 && ` • Processing time: ${currentData.googleAds.processingTime}s`}
                    </>
                  ) : (
                    <>
                      🔄 Fresh data from API • Just fetched
                      {currentData.processingTime && ` • Processing time: ${currentData.processingTime}s`}
                    </>
                  )}
                </small>
              </div>
            )}

            {/* Google Ads Error or No Data */}
            {(!currentData.googleAds?.success || currentData.googleAds?.error) && (
              <div className="text-center py-2" style={{ borderTop: '1px solid rgba(255,255,255,0.2)', borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                <div className="mb-2">
                  <small style={{ color: '#ffcccb' }}>
                    Google Ads: {currentData.googleAds?.error === 'No data available - fetch required' ? 
                      'No data in database' :
                      currentData.googleAds?.error?.includes('Rate limit') ? 
                      'Rate limited - try again later' :
                      currentData.googleAds?.error?.includes('test accounts') ? 
                      'Developer token limited to test accounts. Apply for Basic Access in Google Ads API Center.' : 
                      currentData.googleAds?.error || 'No data available'
                    }
                  </small>
                </div>
                {isDatePickerMode && selectedDate ? (
                  <div>
                    <small style={{ color: 'rgba(255,255,255,0.7)' }}>
                      Use "Fetch Fresh" button above to get Google Ads data for this date
                    </small>
                  </div>
                ) : (
                  <div>
                    <button 
                      className="btn btn-sm btn-outline-light"
                      onClick={fetchFreshGoogleAdsData}
                      disabled={currentData.googleAds?.loading}
                      style={{ borderColor: 'rgba(255,255,255,0.5)', fontSize: '12px' }}
                      title="Fetch fresh Google Ads data from API (takes ~80 seconds)"
                    >
                      {currentData.googleAds?.loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" style={{ width: '10px', height: '10px' }}></span>
                          Fetching... (~80s)
                        </>
                      ) : (
                        <><i className="fa-solid fa-rotate me-1"></i>Fetch Google Ads Data</>
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}
            
            {/* API Breakdown */}
            <div className="row mt-4">
              {Object.entries(currentData.apiBreakdown).map(([apiName, data]) => (
                <div key={apiName} className="col-6 col-md-4 col-lg-2 mb-2">
                  <div className="text-center p-2" style={{ 
                    background: 'rgba(255,255,255,0.1)', 
                    borderRadius: '8px',
                    border: '1px solid rgba(255,255,255,0.2)'
                  }}>
                    <div className="small fw-bold" style={{ color: 'white' }}>
                      {apiName}
                    </div>
                    <div className="small" style={{ color: data.error ? '#ffcccb' : 'rgba(255,255,255,0.9)' }}>
                      {data.error ? 'Error' : formatCurrency(data.revenue)}
                    </div>
                    <div className="small" style={{ color: 'rgba(255,255,255,0.7)' }}>
                      {data.error ? '-' : `${data.conversions} conv`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {currentData.fetchedAt && (
          <div className="text-center mt-3">
            <small style={{ color: 'rgba(255,255,255,0.7)' }}>
              Last updated: {(() => {
                try {
                  return new Date(currentData.fetchedAt).toLocaleTimeString();
                } catch (e) {
                  return 'Unknown';
                }
              })()} • 
              {isDatePickerMode ? (
                <> Use "Fetch Fresh" button for latest data</>
              ) : (
                <> Use refresh button for latest data</>
              )}
              {currentData.processingTime && (
                <> • Processing time: {currentData.processingTime}s</>
              )}
            </small>
          </div>
        )}
      </div>
    </div>
  );
};

export default GrandTotalCard; 